import { type FormEvent, useState } from 'react';
import { AppShell } from '../components/AppShell.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorAlert } from '../components/ErrorAlert.tsx';
import { PageHeader } from '../components/PageHeader.tsx';
import { toast } from '../components/Toast.tsx';
import { Button, Card, CardBody, CardHeader, Input, Spinner } from '../components/ui/index.ts';
import { useAsync } from '../hooks/useAsync.ts';
import { useTenantContext } from '../hooks/useTenantContext.ts';
import { PRESET_ROLES, usersApi } from '../lib/users-api.ts';

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super admin',
  ...Object.fromEntries(PRESET_ROLES.map((r) => [r.id, r.label])),
};

/**
 * Gestión de usuarios del panel.
 *
 * Hasta ahora, dar acceso a alguien de un cliente exigía tocar Cognito y
 * DynamoDB a mano. Esta pantalla es lo que permite que la clínica opere su
 * propio CRM.
 *
 * El backend filtra por scope: un administrador de tenant sólo ve y crea
 * usuarios de su tenant, y no puede asignar super_admin.
 */
export function UsersPage() {
  const { activeTenantId, tenantIds } = useTenantContext();
  const { data, status, error, refetch } = useAsync(() => usersApi.list());
  const [inviting, setInviting] = useState(false);

  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<string>('operator');
  const [tenantId, setTenantId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const users = data?.users ?? [];
  const scopeElegido = tenantId || activeTenantId || '';

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    if (!scopeElegido) {
      toast.error('Elegí a qué tenant pertenece la persona');
      return;
    }
    setSaving(true);
    try {
      await usersApi.create({
        email: email.trim(),
        assignedRoles: [roleId],
        tenantScope: [scopeElegido],
      });
      toast.success(`Invitación enviada a ${email.trim()}`);
      setEmail('');
      setInviting(false);
      refetch();
    } catch (err) {
      toast.error(`No se pudo invitar: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell>
      <div className="flex flex-col h-full animate-fade-in bg-surface-sunken">
        <PageHeader
          title="Usuarios"
          subtitle="Quién puede entrar al panel y qué puede hacer"
          actions={
            !inviting && (
              <Button
                id="users-invite"
                variant="primary"
                size="sm"
                onClick={() => setInviting(true)}
              >
                Invitar persona
              </Button>
            )
          }
        />

        <div className="flex-1 overflow-auto p-6 space-y-6">
          {inviting && (
            <Card variant="elevated">
              <CardHeader>
                <h2 className="text-h5 text-content-primary">Invitar persona</h2>
                <p className="text-caption text-content-tertiary">
                  Recibe un correo con una contraseña temporal que deberá cambiar al entrar.
                </p>
              </CardHeader>
              <CardBody>
                <form onSubmit={handleInvite} className="space-y-4" noValidate>
                  <Input
                    id="user-email"
                    label="Correo electrónico"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="persona@clinica.com"
                  />

                  <fieldset>
                    <legend className="block text-body-sm font-medium text-content-secondary mb-2">
                      Rol
                    </legend>
                    <div className="space-y-2">
                      {PRESET_ROLES.map((r) => (
                        <label
                          key={r.id}
                          className="flex items-start gap-3 rounded-lg border border-edge-subtle p-3 cursor-pointer hover:bg-surface-sunken dark:hover:bg-neutral-800"
                        >
                          <input
                            type="radio"
                            name="role"
                            value={r.id}
                            checked={roleId === r.id}
                            onChange={() => setRoleId(r.id)}
                            className="mt-1"
                          />
                          <span>
                            <span className="block text-body-sm font-medium text-content-primary">
                              {r.label}
                            </span>
                            <span className="block text-xs text-content-tertiary">{r.hint}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  {tenantIds.length > 1 && (
                    <label className="flex flex-col gap-1">
                      <span className="text-body-sm font-medium text-content-secondary">
                        Tenant
                      </span>
                      <select
                        id="user-tenant"
                        value={scopeElegido}
                        onChange={(e) => setTenantId(e.target.value)}
                        className="h-10 rounded-lg border border-edge bg-surface-base px-3 text-body-sm text-content-primary"
                      >
                        {tenantIds.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-content-tertiary">
                        Sólo verá los datos de este tenant.
                      </span>
                    </label>
                  )}

                  <div className="flex gap-2">
                    <Button
                      id="user-save"
                      type="submit"
                      variant="primary"
                      size="sm"
                      loading={saving}
                    >
                      Enviar invitación
                    </Button>
                    <Button
                      id="user-cancel"
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setInviting(false)}
                      disabled={saving}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          )}

          {status === 'error' && (
            <ErrorAlert message={`No se pudieron cargar los usuarios: ${error ?? 'error'}`} />
          )}

          {(status === 'loading' || status === 'idle') && (
            <div className="flex justify-center py-16">
              <Spinner size="lg" />
            </div>
          )}

          {status === 'success' && (
            <Card variant="default">
              <CardBody>
                {users.length === 0 ? (
                  <EmptyState message="Todavía no hay usuarios. Invitá a alguien para que pueda entrar al panel." />
                ) : (
                  <table className="w-full text-body-sm">
                    <thead>
                      <tr className="text-left text-content-tertiary border-b border-edge-subtle">
                        <th className="pb-2 font-medium">Correo</th>
                        <th className="pb-2 font-medium">Rol</th>
                        <th className="pb-2 font-medium">Alcance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.pk} className="border-b border-edge-subtle last:border-0">
                          <td className="py-3 text-content-primary">{u.email}</td>
                          <td className="py-3 text-content-secondary">
                            {(u.assignedRoles ?? []).map((r) => ROLE_LABELS[r] ?? r).join(', ') ||
                              '—'}
                          </td>
                          <td className="py-3 text-content-secondary">
                            {(u.tenantScope ?? []).length === 0
                              ? 'Todos los tenants'
                              : (u.tenantScope ?? []).join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </AppShell>
  );
}
